import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Send, ShieldCheck, Calendar, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';

const ContactSection = () => {
  const prefersReducedMotion = useReducedMotion();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    investorType: '',
    investmentAmount: '',
    marketingConsent: false,
    serviceConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success('Thank you! Our team will contact you within 24 hours.');
    setFormData({ 
      name: '', email: '', phone: '', investorType: '', investmentAmount: '', 
      marketingConsent: false, serviceConsent: false 
    });
    setIsSubmitting(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Three key benefits only
  const benefits = [
    'Private Placement Memorandum',
    'Track Record & Case Studies',
    'Direct Access to Fund Manager'
  ];

  return (
    <section id="contact" className="relative py-24 lg:py-32 overflow-hidden bg-[#0A1628]">
      {/* Minimal background accent */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37]/[0.02] rounded-full blur-[150px] pointer-events-none" />

      <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
          
          {/* ==================== LEFT COLUMN - VALUE PROPOSITION ==================== */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={containerVariants}
            className="lg:sticky lg:top-32"
          >
            {/* Investor Badge */}
            <motion.div 
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6 bg-[#D4AF37]/10 border border-[#D4AF37]/20"
              variants={itemVariants}
            >
              <ShieldCheck className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs font-semibold text-[#D4AF37] uppercase tracking-wider">Investors</span>
            </motion.div>
            
            {/* Heading */}
            <motion.h2 
              className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-5"
              variants={itemVariants}
            >
              Start Your{' '}
              <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                Investment Journey
              </span>
            </motion.h2>
            
            {/* Concise value prop - 2 lines max */}
            <motion.p 
              className="text-white/50 text-base leading-relaxed mb-10 max-w-md"
              variants={itemVariants}
            >
              Request our investment materials and speak directly with our team about Texas real estate opportunities.
            </motion.p>

            {/* Simple 3-point benefits */}
            <motion.div 
              className="mb-10"
              variants={itemVariants}
            >
              <p className="text-white/40 text-sm mb-4">Upon qualification, you'll receive:</p>
              <div className="space-y-3">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                    <span className="text-white/70 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Secondary CTA - Subtle link */}
            <motion.div variants={itemVariants}>
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 text-white/40 hover:text-[#D4AF37] text-sm transition-colors duration-300 group"
              >
                <Calendar className="h-4 w-4" />
                <span>Or schedule a call directly</span>
                <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>

          {/* ==================== RIGHT COLUMN - FORM ==================== */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div 
              className="rounded-3xl p-8 lg:p-10 relative overflow-hidden border border-white/[0.06] bg-[#111D35]/30"
              style={{
                backdropFilter: 'blur(20px)',
              }}
            >
              {/* Top accent line */}
              <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
              
              {/* Form Header */}
              <div className="mb-8">
                <h3 className="text-white text-xl font-bold mb-2">Request Investment Materials</h3>
                <p className="text-white/40 text-sm">We'll respond within one business day.</p>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('name')}
                    onBlur={() => setFocusedField(null)}
                    required
                    data-testid="contact-name-input"
                    className={`w-full bg-[#0A1628]/50 border text-white placeholder:text-white/25 rounded-xl h-14 px-5 text-base transition-all duration-300 ${
                      focusedField === 'name' 
                        ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/10' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  />
                </div>

                {/* Email */}
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    required
                    data-testid="contact-email-input"
                    className={`w-full bg-[#0A1628]/50 border text-white placeholder:text-white/25 rounded-xl h-14 px-5 text-base transition-all duration-300 ${
                      focusedField === 'email' 
                        ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/10' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  />
                </div>

                {/* Phone */}
                <div>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('phone')}
                    onBlur={() => setFocusedField(null)}
                    data-testid="contact-phone-input"
                    className={`w-full bg-[#0A1628]/50 border text-white placeholder:text-white/25 rounded-xl h-14 px-5 text-base transition-all duration-300 ${
                      focusedField === 'phone' 
                        ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/10' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  />
                </div>
                
                {/* Investor Type */}
                <div>
                  <select
                    name="investorType"
                    value={formData.investorType}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('investorType')}
                    onBlur={() => setFocusedField(null)}
                    data-testid="contact-investor-type-select"
                    className={`w-full bg-[#0A1628]/50 border text-white/70 rounded-xl h-14 px-5 text-base transition-all duration-300 appearance-none cursor-pointer ${
                      focusedField === 'investorType' 
                        ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/10' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 1rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.25em 1.25em'
                    }}
                  >
                    <option value="" className="bg-[#111D35]">Investor Type</option>
                    <option value="individual" className="bg-[#111D35]">High Net Worth Individual</option>
                    <option value="family-office" className="bg-[#111D35]">Family Office</option>
                    <option value="ria" className="bg-[#111D35]">RIA / Wealth Manager</option>
                    <option value="institutional" className="bg-[#111D35]">Institutional Investor</option>
                  </select>
                </div>

                {/* Investment Range */}
                <div>
                  <select
                    name="investmentAmount"
                    value={formData.investmentAmount}
                    onChange={handleChange}
                    onFocus={() => setFocusedField('investmentAmount')}
                    onBlur={() => setFocusedField(null)}
                    data-testid="contact-investment-amount-select"
                    className={`w-full bg-[#0A1628]/50 border text-white/70 rounded-xl h-14 px-5 text-base transition-all duration-300 appearance-none cursor-pointer ${
                      focusedField === 'investmentAmount' 
                        ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/10' 
                        : 'border-white/10 hover:border-white/20'
                    }`}
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                      backgroundPosition: 'right 1rem center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '1.25em 1.25em'
                    }}
                  >
                    <option value="" className="bg-[#111D35]">Investment Range</option>
                    <option value="100-250k" className="bg-[#111D35]">$100,000 – $250,000</option>
                    <option value="250-500k" className="bg-[#111D35]">$250,000 – $500,000</option>
                    <option value="500k-1m" className="bg-[#111D35]">$500,000 – $1,000,000</option>
                    <option value="1m+" className="bg-[#111D35]">$1,000,000+</option>
                  </select>
                </div>
                
                {/* SMS Consent Checkboxes - Two separate checkboxes */}
                <div className="pt-4 mt-2 border-t border-white/5 space-y-3">
                  {/* Checkbox 1 - Marketing Consent */}
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <label className="flex items-start gap-3 cursor-pointer group" htmlFor="marketing-consent-home">
                      <div className="relative flex-shrink-0 mt-0.5">
                        <input
                          type="checkbox"
                          name="marketingConsent"
                          checked={formData.marketingConsent}
                          onChange={handleChange}
                          className="sr-only peer"
                          data-testid="home-marketing-consent"
                          id="marketing-consent-home"
                        />
                        <div 
                          className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200 ${
                            formData.marketingConsent 
                              ? 'bg-[#F97316] border-[#F97316]' 
                              : 'border-white/40 bg-transparent hover:border-[#F97316]/60'
                          }`}
                        >
                          {formData.marketingConsent && (
                            <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-white/50 text-xs leading-relaxed">
                        I consent to receive marketing text messages from ASAP Capital at the phone number provided. Message frequency: up to 6 messages per month. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                      </span>
                    </label>
                  </div>

                  {/* Checkbox 2 - Service/Non-Marketing Consent */}
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <label className="flex items-start gap-3 cursor-pointer group" htmlFor="service-consent-home">
                      <div className="relative flex-shrink-0 mt-0.5">
                        <input
                          type="checkbox"
                          name="serviceConsent"
                          checked={formData.serviceConsent}
                          onChange={handleChange}
                          className="sr-only peer"
                          data-testid="home-service-consent"
                          id="service-consent-home"
                        />
                        <div 
                          className={`w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200 ${
                            formData.serviceConsent 
                              ? 'bg-[#F97316] border-[#F97316]' 
                              : 'border-white/40 bg-transparent hover:border-[#F97316]/60'
                          }`}
                        >
                          {formData.serviceConsent && (
                            <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          )}
                        </div>
                      </div>
                      <span className="text-white/50 text-xs leading-relaxed">
                        I consent to receive non-marketing text messages from ASAP Capital regarding my inquiry, consultation scheduling, appointment confirmations, and service-related updates. Message frequency: up to 6 messages per month (plus replies to my requests). Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.
                      </span>
                    </label>
                  </div>

                  {/* Terms Agreement Text */}
                  <p className="text-white/40 text-xs text-center pt-1">
                    By submitting this form, you agree to our{' '}
                    <Link to="/terms" className="text-[#F97316] hover:underline">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-[#F97316] hover:underline">Privacy Policy</Link>.
                  </p>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                    className="cta-contact rounded-full h-14"
                  >
                    {isSubmitting ? (
                      <motion.div 
                        className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    ) : (
                      <>
                        Request Investment Materials
                        <Send className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
              
              {/* Minimal Disclaimer */}
              <p className="text-white/20 text-xs mt-6 text-center">
                Not an offer to sell. Investments made via Private Placement Memorandum only.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
